import { useState, useEffect, useCallback } from "react";
import { useLang } from "../../i18n/LanguageContext";

const STORAGE_KEY = "onix_cookie_prefs";

const readPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Toggle = ({ checked, onChange, disabled, label, id }) => (
  <button
    id={id}
    data-testid={`cookie-toggle-${id}`}
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative w-11 h-6 border transition-colors duration-200 shrink-0 ${
      checked ? "bg-onix-white border-onix-white" : "bg-transparent border-onix-line"
    } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
  >
    <span
      className={`absolute top-[3px] w-4 h-4 transition-transform duration-200 ${
        checked ? "translate-x-[24px] bg-onix-black" : "translate-x-[3px] bg-onix-silver"
      }`}
      aria-hidden="true"
    />
  </button>
);

export const CookieConsent = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [personalization, setPersonalization] = useState(false);

  useEffect(() => {
    const prefs = readPrefs();
    if (!prefs || !prefs.decided) setVisible(true);
    const open = () => {
      const p = readPrefs();
      setAnalytics(Boolean(p?.analytics));
      setPersonalization(Boolean(p?.personalization));
      setConfiguring(true);
      setVisible(true);
    };
    window.addEventListener("onix-open-cookies", open);
    return () => window.removeEventListener("onix-open-cookies", open);
  }, []);

  const save = useCallback((prefs) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...prefs, necessary: true, decided: true, updatedAt: new Date().toISOString() })
    );
    window.dispatchEvent(new CustomEvent("onix-consent-updated"));
    setVisible(false);
    setConfiguring(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="region"
      aria-label={t.cookies.region}
      className="fixed bottom-0 left-0 right-0 z-[70] bg-onix-black2 border-t border-onix-line"
    >
      <div className="onix-shell py-6 md:py-7">
        <div className="grid lg:grid-cols-12 gap-6 items-start">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-2">{t.cookies.title}</p>
            <p className="text-sm leading-relaxed text-onix-silver max-w-2xl">{t.cookies.text}</p>
          </div>
          <div className="lg:col-span-5 flex flex-wrap items-center gap-3 lg:justify-end">
            <button data-testid="cookie-accept" className="btn-onix-primary !px-5 !py-2.5" onClick={() => save({ analytics: true, personalization: true })}>
              {t.cookies.accept}
            </button>
            <button data-testid="cookie-reject" className="btn-onix-primary !px-5 !py-2.5" onClick={() => save({ analytics: false, personalization: false })}>
              {t.cookies.reject}
            </button>
            <button
              data-testid="cookie-configure"
              className="btn-onix-secondary !px-5 !py-2.5"
              aria-expanded={configuring}
              onClick={() => setConfiguring((v) => !v)}
            >
              {t.cookies.configure}
            </button>
          </div>
        </div>

        {configuring && (
          <div data-testid="cookie-config-panel" className="mt-6 border-t border-onix-line pt-6 grid md:grid-cols-3 gap-6">
            <div className="flex items-start justify-between gap-4 border border-onix-line p-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-onix-white mb-1">{t.cookies.necessary.name}</p>
                <p className="text-xs text-onix-gray leading-relaxed">{t.cookies.necessary.desc}</p>
              </div>
              <Toggle id="necessary" label={t.cookies.necessary.aria} checked disabled onChange={() => {}} />
            </div>
            <div className="flex items-start justify-between gap-4 border border-onix-line p-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-onix-white mb-1">{t.cookies.analytics.name}</p>
                <p className="text-xs text-onix-gray leading-relaxed">{t.cookies.analytics.desc}</p>
              </div>
              <Toggle id="analytics" label={t.cookies.analytics.aria} checked={analytics} onChange={setAnalytics} />
            </div>
            <div className="flex items-start justify-between gap-4 border border-onix-line p-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-onix-white mb-1">{t.cookies.personalization.name}</p>
                <p className="text-xs text-onix-gray leading-relaxed">{t.cookies.personalization.desc}</p>
              </div>
              <Toggle id="personalization" label={t.cookies.personalization.aria} checked={personalization} onChange={setPersonalization} />
            </div>
            <div className="md:col-span-3">
              <button data-testid="cookie-save-preferences" className="btn-onix-secondary !px-5 !py-2.5" onClick={() => save({ analytics, personalization })}>
                {t.cookies.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
